// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import React, { useState } from 'react';
import { db, auth } from '../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations';
import { createBakery, generateUniqueSubdomain } from '../utils/bakeryUtils';
import { geocodePostcodeCity } from '../utils/weatherProvider';
import { getRegions, regionLabelKey, isValidRegion } from '../utils/regions';
import { isPostcodeFirst } from '../utils/addressUtils';
import { ensureSeededIngredients } from '../utils/ingredientsUtils';
import { validateEmail, validatePassword, validateRequired, validateMatch, validatePhone } from '../utils/formValidationUtils';
import { Globe, Mail, User, Phone, Building2, MapPin } from 'lucide-react';
import Button from './Button';
import Alert from './ui/Alert';
import InputField from './forms/InputField';
import PasswordField from './forms/PasswordField';
import { privacyPolicyData, dpaData } from '../data/privacyPolicyData';
import { termsOfServiceData } from '../data/termofservice';
import { useToast } from '../contexts/ToastContext';
import emailjs from '@emailjs/browser';

const COUNTRIES = [
  { code: 'FR', name: 'France', nameFr: 'France', currency: 'EUR', symbol: '€', flag: '🇫🇷', phonePlaceholder: '+33 6 12 34 56 78' },
  { code: 'CA', name: 'Canada', nameFr: 'Canada', currency: 'CAD', symbol: 'CA$', flag: '🇨🇦', phonePlaceholder: '+1 (555) 123-4567' },
  { code: 'US', name: 'United States', nameFr: 'États-Unis', currency: 'USD', symbol: '$', flag: '🇺🇸', phonePlaceholder: '+1 (555) 123-4567' },
  { code: 'GB', name: 'United Kingdom', nameFr: 'Grande-Bretagne', currency: 'GBP', symbol: '£', flag: '🇬🇧', phonePlaceholder: '+44 20 1234 5678' }
];

export default function BakerRegistration({ onSuccess, onSwitchToCustomer }) {
  const { language, setLanguage } = useLanguage();
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    bakeryName: '',
    country: '',
    postalCode: '',
    city: '',
    region: '',
    privacyPolicyAccepted: false,
    dpaAccepted: false,
    tosAccepted: false
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showDpaModal, setShowDpaModal] = useState(false);
  const [showTosModal, setShowTosModal] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Province/state only exists for Canada / US: drop it when the country no longer fits
      ...(field === 'country' && !isValidRegion(value, prev.region) ? { region: '' } : {})
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validation functions
  const validateBakeryName = () => {
    const validation = validateRequired(formData.bakeryName, 'Bakery name');
    if (!validation.isValid) {
      setErrors(prev => ({ ...prev, bakeryName: validation.message }));
      return false;
    }
    if (formData.bakeryName.length < 2) {
      setErrors(prev => ({ ...prev, bakeryName: language === 'fr' ? 'Le nom doit contenir au moins 2 caractères' : 'Name must be at least 2 characters' }));
      return false;
    }
    setErrors(prev => ({ ...prev, bakeryName: '' }));
    return true;
  };

  const validateName = () => {
    const validation = validateRequired(formData.name, 'Name');
    setErrors(prev => ({ ...prev, name: validation.isValid ? '' : validation.message }));
    return validation.isValid;
  };

  const validateEmailField = () => {
    const validation = validateEmail(formData.email);
    setErrors(prev => ({ ...prev, email: validation.isValid ? '' : validation.message }));
    return validation.isValid;
  };

  const validatePhoneField = () => {
    const validation = validatePhone(formData.phone, formData.country);
    setErrors(prev => ({ ...prev, phone: validation.isValid ? '' : validation.message }));
    return validation.isValid;
  };

  const validatePasswordField = () => {
    const validation = validatePassword(formData.password);
    setErrors(prev => ({ ...prev, password: validation.isValid ? '' : validation.message }));
    return validation.isValid;
  };

  const validateConfirmPassword = () => {
    const validation = validateMatch(formData.password, formData.confirmPassword, 'Password');
    setErrors(prev => ({ ...prev, confirmPassword: validation.isValid ? '' : validation.message }));
    return validation.isValid;
  };

  // EmailJS configuration
  const EMAILJS_SERVICE_ID = 'service_19m8qkg';
  const EMAILJS_PUBLIC_KEY = '9Z1htA3iQR01w1YrU';
  const EMAILJS_WELCOME_TEMPLATE = 'template_n47p9ff';

  /**
   * Send welcome email to new baker
   */
  const sendWelcomeEmail = async (bakerData, bakeryData) => {
    try {
      // Determine language based on country
      const emailLanguage = bakeryData.country === 'FR' ? 'fr' : 'en';

      // Calculate grace period end date
      const gracePeriodEnd = new Date();
      gracePeriodEnd.setDate(gracePeriodEnd.getDate() + 7);

      const gracePeriodEndFormatted = gracePeriodEnd.toLocaleDateString(
        emailLanguage === 'fr' ? 'fr-FR' : 'en-US',
        { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
      );

      // Prepare email content based on language
      const emailContent = emailLanguage === 'fr' ? {
        subject: 'Bienvenue sur Digi-Bakery ! Votre boulangerie est prête',
        greeting: `Bonjour ${bakerData.name},`,
        welcomeMessage: `Bienvenue sur Digi-Bakery ! Votre boulangerie "${bakeryData.name}" est maintenant en ligne à :`,
        loginUrl: `https://digi-bakery.com/${bakeryData.subdomain}`,
        gracePeriodTitle: '🎁 Période d\'essai gratuite',
        gracePeriodMessage: `Vous avez 7 jours d'accès gratuit pour explorer toutes les fonctionnalités !`,
        gracePeriodEnd: `Votre période gratuite se termine le ${gracePeriodEndFormatted}.`,
        quickStartTitle: 'Démarrage rapide :',
        quickStart1: '• Configurez votre boulangerie dans les paramètres (Vitrine, préférences, etc...)',
        quickStart2: '• Créez vos recettes et produits',
        quickStart3: '• Planifiez une production',
        quickStart4: '• Cliquez sur le bouton d\'aide sur chaque page pour en savoir plus ou consultez notre chaîne YouTube pour des tutoriels vidéo',
        youtubeUrl: 'https://www.youtube.com/channel/UCwFP69NOBeHfe1X5MYWqJXQ',
        youtubeButtonText: '📺 Voir les tutoriels vidéo',
        subscriptionMessage: 'Après votre période gratuite, abonnez-vous pour continuer à utiliser votre boulangerie :',
        subscriptionUrl: `https://digi-bakery.com/${bakeryData.subdomain}/settings?tab=subscription`,
        subscriptionButtonText: 'Gérer mon abonnement',
        supportTitle: 'Besoin d\'aide ?',
        supportMessage: 'Répondez à cet email ou contactez-nous à support@digi-bakery.com',
        closing: 'Bonne cuisine !',
        signature: 'L\'équipe Digi-Bakery'
      } : {
        subject: 'Welcome to Digi-Bakery! Your bakery is ready',
        greeting: `Hi ${bakerData.name},`,
        welcomeMessage: `Welcome to Digi-Bakery! Your bakery "${bakeryData.name}" is now live at:`,
        loginUrl: `https://digi-bakery.com/${bakeryData.subdomain}`,
        gracePeriodTitle: '🎁 Free Trial Period',
        gracePeriodMessage: `You have 7 days of free access to explore all features!`,
        gracePeriodEnd: `Your free period ends on ${gracePeriodEndFormatted}.`,
        quickStartTitle: 'Quick Start:',
        quickStart1: '• Setup your bakery in the settings (Storefront, preferences, etc...)',
        quickStart2: '• Create your recipes and products',
        quickStart3: '• Plan a production',
        quickStart4: '• Click on the help button on each page to know better or visit our YouTube channel for video tutorials',
        youtubeUrl: 'https://www.youtube.com/channel/UCwFP69NOBeHfe1X5MYWqJXQ',
        youtubeButtonText: '📺 Watch Video Tutorials',
        subscriptionMessage: 'After your free period, subscribe to keep your bakery running:',
        subscriptionUrl: `https://digi-bakery.com/${bakeryData.subdomain}/settings?tab=subscription`,
        subscriptionButtonText: 'Manage Subscription',
        supportTitle: 'Need help?',
        supportMessage: 'Reply to this email or contact us at support@digi-bakery.com',
        closing: 'Happy baking!',
        signature: 'The Digi-Bakery Team'
      };

      // Send email via EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_WELCOME_TEMPLATE,
        {
          to_email: bakerData.email,
          to_name: bakerData.name,
          subject: emailContent.subject,
          logoUrl: 'https://digi-bakery.com/lockup-stacked-color.png',
          greeting: emailContent.greeting,
          welcomeMessage: emailContent.welcomeMessage,
          loginUrl: emailContent.loginUrl,
          gracePeriodTitle: emailContent.gracePeriodTitle,
          gracePeriodMessage: emailContent.gracePeriodMessage,
          gracePeriodEnd: emailContent.gracePeriodEnd,
          quickStartTitle: emailContent.quickStartTitle,
          quickStart1: emailContent.quickStart1,
          quickStart2: emailContent.quickStart2,
          quickStart3: emailContent.quickStart3,
          quickStart4: emailContent.quickStart4,
          youtubeUrl: emailContent.youtubeUrl,
          youtubeButtonText: emailContent.youtubeButtonText,
          subscriptionMessage: emailContent.subscriptionMessage,
          subscriptionUrl: emailContent.subscriptionUrl,
          subscriptionButtonText: emailContent.subscriptionButtonText,
          supportTitle: emailContent.supportTitle,
          supportMessage: emailContent.supportMessage,
          closing: emailContent.closing,
          signature: emailContent.signature,
          bakeryName: bakeryData.name
        },
        EMAILJS_PUBLIC_KEY
      );

      console.log('✅ Welcome email sent successfully to', bakerData.email);
    } catch (error) {
      console.error('❌ Error sending welcome email:', error);
      // Don't throw - we don't want to block registration if email fails
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrors({});

    // Validate all fields
    const isBakeryNameValid = validateBakeryName();
    const isNameValid = validateName();
    const isEmailValid = validateEmailField();
    const isPhoneValid = validatePhoneField();
    const isPasswordValid = validatePasswordField();
    const isConfirmPasswordValid = validateConfirmPassword();

    if (!isBakeryNameValid || !isNameValid || !isEmailValid || !isPhoneValid || !isPasswordValid || !isConfirmPasswordValid) {
      showToast(t('fillAllFields', language), 'error');
      return;
    }

    if (!formData.country) {
      showToast(language === 'fr' ? 'Veuillez sélectionner un pays' : 'Please select a country', 'error');
      return;
    }

    if (!formData.postalCode.trim() || !formData.city.trim()) {
      setErrors(prev => ({
        ...prev,
        postalCode: formData.postalCode.trim() ? '' : t('required', language),
        city: formData.city.trim() ? '' : t('required', language)
      }));
      showToast(t('postalCodeCityRequired', language), 'error');
      return;
    }

    if (!formData.privacyPolicyAccepted) {
      showToast(t('privacyPolicyRequired', language), 'error');
      return;
    }

    if (!formData.dpaAccepted) {
      showToast(language === 'fr' ? 'Acceptation du DPA requise' : 'DPA acceptance required', 'error');
      return;
    }

    if (!formData.tosAccepted) {
      showToast(t('tosRequired', language), 'error');
      return;
    }

    setLoading(true);

    try {

      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);

      // Generate unique subdomain and create bakery
      const subdomain = await generateUniqueSubdomain(formData.bakeryName);
      const selectedCountry = COUNTRIES.find(c => c.code === formData.country);
      const postalCode = formData.postalCode.trim();
      const city = formData.city.trim();

      // Locate the bakery for the weather of sales days — never blocks registration
      let weatherLocation = null;
      try {
        const found = await geocodePostcodeCity({ postalCode, city, country: formData.country });
        if (found) {
          weatherLocation = { ...found, query: `${postalCode}|${city}|${formData.country}`.toLowerCase() };
        }
      } catch (_) { /* can be fixed later in Settings */ }

      await createBakery(userCredential.user.uid, {
        name: formData.bakeryName,
        subdomain,
        country: formData.country,
        currency: selectedCountry?.currency || 'USD',
        currencySymbol: selectedCountry?.symbol || '$',
        postalCode,
        city,
        region: isValidRegion(formData.country, formData.region) ? formData.region : '',
        ...(weatherLocation ? { weatherLocation } : {})
      });

      await ensureSeededIngredients(userCredential.user.uid);

      // Create user document
      const userData = {
        email: formData.email,
        name: formData.name,
        phone: formData.phone,
        role: 'baker',
        country: formData.country,
        bakeryId: userCredential.user.uid,
        createdAt: new Date().toISOString(),
        address: '',
        privacyPolicyAcceptedAt: new Date().toISOString(),
        privacyPolicyVersion: '1.0',
        dpaAcceptedAt: new Date().toISOString(),
        dpaVersion: '1.0',
        tosAcceptedAt: new Date().toISOString(),
        tosVersion: '1.0'
      };

      await setDoc(doc(db, 'users', userCredential.user.uid), userData);

      // Force token refresh to ensure auth context is ready for Cloud Functions
      await userCredential.user.getIdToken(true);

      // Fetch the bakery data that was just created
      const bakeryDoc = await getDoc(doc(db, 'bakeries', userCredential.user.uid));
      const bakeryData = bakeryDoc.data();

      // Send welcome email (don't await - let it happen in background)
      sendWelcomeEmail(userData, bakeryData).catch(err =>
        console.error('Welcome email failed:', err)
      );

      // Enter app on grace period — subscription reminder banner handles the rest
      if (onSuccess) {
        onSuccess();
      }
      window.location.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const privacyContent = privacyPolicyData[language] || privacyPolicyData.en;
  const dpaContent = dpaData[language] || dpaData.en;
  const tosContent = termsOfServiceData[language] || termsOfServiceData.en;

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-[var(--status-warning-soft)] via-[var(--bg-secondary)] to-[var(--status-warning-soft)] flex">
        {/* Language Selector */}
        <div className="absolute top-4 right-4 z-10">
          <Button
            onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
            variant="ghost"
            className="flex items-center gap-2 bg-[var(--surface-card)]/90 backdrop-blur-sm text-[var(--text-primary)] px-4 py-2 rounded-full text-sm font-medium shadow-lg border border-[var(--border-subtle)]"
          >
            <Globe size={16} />
            {language === 'en' ? 'FR' : 'EN'}
          </Button>
        </div>

        {/* Left Side - Hero Section */}
        <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12">
          <div className="max-w-xl">
            <div className="inline-block p-6 bg-gradient-to-br from-cream to-cream-dark rounded-3xl shadow-soft-lg mb-8">
              <img
                src="/digibakery-logo.svg"
                alt="Digi-Bakery"
                className="h-16 w-auto mx-auto"
                style={{
                  filter: 'invert(54%) sepia(32%) saturate(727%) hue-rotate(332deg) brightness(91%) contrast(85%)'
                }}
              />
            </div>

            <h1 className="text-5xl font-black text-[var(--text-primary)] mb-6 tracking-tight leading-tight">
              <span className="text-[var(--action-primary)]">Digi-Bakery</span>
              <br />
              <span className="text-3xl font-bold text-[var(--text-primary)]">
                {language === 'fr'
                  ? 'La plateforme numérique pour boulangers'
                  : 'The Digital Platform for Bakers'
                }
              </span>
            </h1>

            <p className="text-lg text-[var(--text-secondary)] leading-relaxed font-medium">
              {language === 'fr'
                ? 'Créez votre boutique en ligne, gérez vos commandes, organisez votre production et développez votre clientèle avec notre plateforme tout-en-un conçue spécialement pour les artisans boulangers.'
                : 'Create your online shop, manage orders, organize production, and grow your customer base with our all-in-one platform designed specifically for artisan bakers.'
              }
            </p>
          </div>
        </div>

        {/* Right Side - Registration Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 lg:p-12">
          <div className="glass-effect rounded-3xl shadow-modern-lg p-8 lg:p-12 w-full max-w-md border border-white/30">
            {/* Mobile Logo - only show on small screens */}
            <div className="lg:hidden text-center mb-8">
              <div className="inline-block p-6 bg-gradient-to-br from-cream to-cream-dark rounded-3xl shadow-soft-lg mb-4">
                <img
                  src="/digibakery-logo.svg"
                  alt="Digi-Bakery"
                  className="h-12 w-auto mx-auto"
                  style={{
                    filter: 'invert(54%) sepia(32%) saturate(727%) hue-rotate(332deg) brightness(91%) contrast(85%)'
                  }}
                />
              </div>
              <h1 className="text-3xl font-black text-[var(--text-primary)] mb-2">
                <span className="text-[var(--action-primary)]">Digi-Bakery</span>
              </h1>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl lg:text-3xl font-black text-[var(--text-primary)] tracking-tight">
                {t('createBakery', language) || 'Create Bakery'}
              </h2>
            </div>

          {error && (
            <Alert tone="danger" className="mb-6">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <InputField
              label={t('bakeryName', language)}
              type="text"
              value={formData.bakeryName}
              onChange={(e) => handleInputChange('bakeryName', e.target.value)}
              onBlur={validateBakeryName}
              error={errors.bakeryName}
              helperText={t('bakeryNameHelp', language)}
              icon={Building2}
              required
              disabled={loading}
              placeholder={t('bakeryNamePlaceholder', language)}
            />

            <div>
              <label className="block text-sm font-bold text-[var(--text-primary)] mb-2">
                {t('countryRegion', language)} *
              </label>
              <select
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                className="w-full border-2 border-[var(--border-subtle)] rounded-2xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[var(--focus-ring)] focus:border-[var(--action-primary)] transition-all duration-300 bg-[var(--surface-card)]/80 backdrop-blur-sm"
                disabled={loading}
                required
              >
                <option value="">
                  {language === 'fr' ? 'Choisissez un pays' : 'Choose a country'}
                </option>
                {COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {language === 'fr' ? c.nameFr : c.name}
                  </option>
                ))}
              </select>
            </div>

            {(() => {
              // Field order follows the country: "Postcode · Town" (France, Europe) or
              // "Town · Province/State · Postcode" (Canada, US) / "Town · Postcode" (UK)
              const regions = getRegions(formData.country);
              const postcodeFirst = isPostcodeFirst(formData.country) || !formData.country;
              const postcodeField = (
                <InputField
                  key="postalCode"
                  label={t('postalCode', language)}
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  error={errors.postalCode}
                  icon={postcodeFirst ? MapPin : undefined}
                  required
                  disabled={loading}
                  autoComplete="postal-code"
                  placeholder={formData.country === 'CA' ? 'H2X 3K6' : formData.country === 'GB' ? 'SW1A 2AA' : formData.country === 'US' ? '62704' : '74000'}
                />
              );
              const cityField = (
                <InputField
                  key="city"
                  label={t('city', language)}
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  error={errors.city}
                  icon={postcodeFirst ? undefined : MapPin}
                  required
                  disabled={loading}
                  autoComplete="address-level2"
                  placeholder={formData.country === 'CA' ? 'Montréal' : formData.country === 'GB' ? 'London' : formData.country === 'US' ? 'Springfield' : 'Annecy'}
                />
              );
              const regionField = regions && (
                <div key="region">
                  <label className="block text-sm font-semibold text-[var(--text-secondary)] mb-2">
                    {t(regionLabelKey(formData.country), language)}
                  </label>
                  {/* Same look and height as InputField */}
                  <select
                    value={formData.region}
                    onChange={(e) => handleInputChange('region', e.target.value)}
                    className="w-full h-[52px] px-4 rounded-xl border-2 border-[var(--border-subtle)] bg-[var(--surface-card)] text-[var(--text-primary)] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--focus-ring)] focus:border-[var(--brand-terracotta)]"
                    disabled={loading}
                    autoComplete="address-level1"
                  >
                    <option value="">{t('addressRegionChoose', language)}</option>
                    {regions.map(r => (
                      <option key={r.code} value={r.code}>
                        {language === 'fr' ? r.nameFr : r.name} ({r.code})
                      </option>
                    ))}
                  </select>
                </div>
              );
              // The registration card is narrow: Canada / US put the town on its own line,
              // then province/state + postcode side by side
              if (regions) {
                return (
                  <div className="space-y-4">
                    {cityField}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {regionField}
                      {postcodeField}
                    </div>
                  </div>
                );
              }
              const fields = postcodeFirst ? [postcodeField, cityField] : [cityField, postcodeField];
              const cols = postcodeFirst ? 'sm:grid-cols-[160px_minmax(0,1fr)]' : 'sm:grid-cols-[minmax(0,1fr)_160px]';
              return <div className={`grid grid-cols-1 ${cols} gap-4`}>{fields}</div>;
            })()}

            <InputField
              label={t('yourName', language) || 'Your Name'}
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              onBlur={validateName}
              error={errors.name}
              icon={User}
              required
              disabled={loading}
              placeholder={t('fullName', language) || 'Full Name'}
            />

            <InputField
              label={t('email', language)}
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onBlur={validateEmailField}
              error={errors.email}
              icon={Mail}
              required
              disabled={loading}
              placeholder="your@email.com"
            />

            <InputField
              label={t('phone', language)}
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              onBlur={validatePhoneField}
              error={errors.phone}
              icon={Phone}
              required
              disabled={loading}
              placeholder={COUNTRIES.find(c => c.code === formData.country)?.phonePlaceholder || '+1 (555) 123-4567'}
            />

            <PasswordField
              label={t('password', language)}
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              onBlur={validatePasswordField}
              error={errors.password}
              showStrengthIndicator
              showRequirements
              disabled={loading}
              placeholder="••••••••"
            />

            <PasswordField
              label={t('confirmPassword', language) || 'Confirm Password'}
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              onBlur={validateConfirmPassword}
              error={errors.confirmPassword}
              disabled={loading}
              placeholder="••••••••"
            />

            {/* Privacy Policy Checkbox */}
            <div className="flex items-start space-x-3 p-4 bg-[var(--status-warning-soft)] rounded-2xl border border-[var(--border-subtle)]">
              <input
                type="checkbox"
                id="privacyPolicyAccepted"
                checked={formData.privacyPolicyAccepted}
                onChange={(e) => handleInputChange('privacyPolicyAccepted', e.target.checked)}
                className="mt-1 h-4 w-4 text-[var(--action-primary)] focus:ring-[var(--focus-ring)] border-[var(--border-subtle)] rounded"
                disabled={loading}
                required
              />
              <label htmlFor="privacyPolicyAccepted" className="text-sm text-[var(--text-primary)] leading-relaxed">
                {t('privacyPolicyAgreement', language)}
                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-[var(--action-primary)] hover:text-[var(--action-primary-hover)] underline ml-1 font-medium"
                  disabled={loading}
                >
                  {t('privacyPolicy', language)}
                </button>
              </label>
            </div>

            {/* DPA Checkbox */}
            <div className="flex items-start space-x-3 p-4 bg-gradient-to-r from-[var(--bg-secondary)] to-[var(--bg-secondary)] rounded-2xl border border-[var(--border-subtle)]">
              <input
                type="checkbox"
                id="dpaAccepted"
                checked={formData.dpaAccepted}
                onChange={(e) => handleInputChange('dpaAccepted', e.target.checked)}
                className="mt-1 h-4 w-4 text-[var(--action-primary)] focus:ring-[var(--focus-ring)] border-[var(--border-strong)] rounded"
                disabled={loading}
                required
              />
              <label htmlFor="dpaAccepted" className="text-sm text-[var(--text-primary)] leading-relaxed">
                {language === 'fr' ? 'J\'accepte' : 'I accept the'}
                <button
                  type="button"
                  onClick={() => setShowDpaModal(true)}
                  className="text-[var(--action-primary)] hover:text-[var(--text-secondary)] underline ml-1 font-medium"
                  disabled={loading}
                >
                  {language === 'fr' ? 'l\'Accord de Sous-traitance des Données' : 'Data Processing Agreement (DPA)'}
                </button>
              </label>
            </div>

            {/* Terms of Service Checkbox */}
            <div className="flex items-start space-x-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200">
              <input
                type="checkbox"
                id="tosAccepted"
                checked={formData.tosAccepted}
                onChange={(e) => handleInputChange('tosAccepted', e.target.checked)}
                className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-green-300 rounded"
                disabled={loading}
                required
              />
              <label htmlFor="tosAccepted" className="text-sm text-[var(--text-primary)] leading-relaxed">
                {t('tosAgreement', language)}
                <button
                  type="button"
                  onClick={() => setShowTosModal(true)}
                  className="text-green-600 hover:text-green-700 underline ml-1 font-medium"
                  disabled={loading}
                >
                  {t('termsOfService', language)}
                </button>
              </label>
            </div>

            <Button
              type="submit"
              loading={loading}
              disabled={loading}
              className="w-full py-4 rounded-2xl shadow-modern-lg transform hover:scale-105"
            >
              {t('createBakery', language) || 'Create Bakery'}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <Button
              onClick={() => window.location.href = '/login'}
              variant="ghost"
              className="w-full text-sm py-2 rounded-xl"
            >
              {t('alreadyHaveAccount', language) || 'Already have an account?'}{' '}
              <span className="font-medium">{t('loginButton', language) || 'Login'}</span>
            </Button>
            <Button
              onClick={onSwitchToCustomer}
              variant="ghost"
              className="w-full text-sm py-2 rounded-xl"
            >
              {t('justWantToOrder', language) || 'Just want to order?'}{' '}
              <span className="font-medium">{t('registerAsCustomer', language) || 'Register as Customer'}</span>
            </Button>
          </div>
        </div>
      </div>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--surface-card)] rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-white/20 flex flex-col">
            <div className="flex justify-between items-center p-6 border-b bg-[var(--status-warning-soft)] flex-shrink-0">
              <h2 className="text-xl font-bold text-[var(--text-primary)] pr-4 flex-1">{privacyContent.title}</h2>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] bg-[var(--surface-card)] hover:bg-[var(--bg-secondary)] rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold border border-[var(--border-strong)] transition-colors flex-shrink-0"
                aria-label="Close privacy policy"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-[var(--text-secondary)] mb-6 text-sm">{privacyContent.effectiveDate}</p>
              <div className="prose prose-gray max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-[var(--text-primary)] leading-relaxed text-sm">
                  {privacyContent.content.trim()}
                </pre>
              </div>
            </div>
            <div className="flex justify-end p-6 border-t bg-[var(--status-warning-soft)] flex-shrink-0">
              <Button
                onClick={() => setShowPrivacyModal(false)}
                className="px-6 py-3 rounded-2xl"
              >
                {language === 'fr' ? 'Fermer' : 'Close'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* DPA Modal */}
      {showDpaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--surface-card)] rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-white/20 flex flex-col">
            <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-[var(--bg-secondary)] to-[var(--bg-secondary)] flex-shrink-0">
              <h2 className="text-xl font-bold text-[var(--text-primary)] pr-4 flex-1">{dpaContent.title}</h2>
              <button
                onClick={() => setShowDpaModal(false)}
                className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] bg-[var(--surface-card)] hover:bg-[var(--bg-secondary)] rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold border border-[var(--border-strong)] transition-colors flex-shrink-0"
                aria-label="Close DPA"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-[var(--text-secondary)] mb-2 text-sm font-medium">{dpaContent.version}</p>
              <p className="text-[var(--text-secondary)] mb-6 text-sm">{dpaContent.acceptance}</p>
              <div className="prose prose-gray max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-[var(--text-primary)] leading-relaxed text-sm">
                  {dpaContent.content.trim()}
                </pre>
              </div>
            </div>
            <div className="flex justify-end p-6 border-t bg-gradient-to-r from-[var(--bg-secondary)] to-[var(--bg-secondary)] flex-shrink-0">
              <Button
                onClick={() => setShowDpaModal(false)}
                variant="secondary"
                className="px-6 py-3 rounded-2xl"
              >
                {language === 'fr' ? 'Fermer' : 'Close'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Terms of Service Modal */}
      {showTosModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--surface-card)] rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-white/20 flex flex-col">
            <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-green-50 to-emerald-50 flex-shrink-0">
              <h2 className="text-xl font-bold text-[var(--text-primary)] pr-4 flex-1">{tosContent.title}</h2>
              <button
                onClick={() => setShowTosModal(false)}
                className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] bg-[var(--surface-card)] hover:bg-[var(--bg-secondary)] rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold border border-[var(--border-strong)] transition-colors flex-shrink-0"
                aria-label="Close Terms of Service"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-[var(--text-secondary)] mb-2 text-sm font-medium">{tosContent.effectiveDate}</p>
              <p className="text-[var(--text-secondary)] mb-2 text-sm font-medium">{tosContent.version}</p>
              <p className="text-[var(--text-secondary)] mb-6 text-sm">{tosContent.acceptance}</p>
              <div className="prose prose-gray max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-[var(--text-primary)] leading-relaxed text-sm">
                  {tosContent.content.trim()}
                </pre>
              </div>
            </div>
            <div className="flex justify-end p-6 border-t bg-gradient-to-r from-green-50 to-emerald-50 flex-shrink-0">
              <Button
                onClick={() => setShowTosModal(false)}
                variant="ghost"
                className="px-6 py-3 rounded-2xl"
              >
                {language === 'fr' ? 'Fermer' : 'Close'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
